import { useEffect, useMemo, useState } from 'react';
import { Package, Search, MapPin, Plus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { invApi, invInput, StockBadge, errMsg } from './invApi';
import { NewPartForm } from './NewPartForm';

const ItemRow = ({ item, reason, onAdd }) => {
  const [q, setQ] = useState(1);
  return (
    <div className="flex items-center gap-2 bg-slate-800/60 rounded-lg px-3 py-2" data-testid={`picker-item-${item.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-white truncate">{item.name} <span className="text-slate-500">{item.part_number}</span></p>
        <p className="text-[10px] text-slate-400 truncate">
          {item.location_label && <span className="text-amber-300 font-mono mr-2"><MapPin size={9} className="inline" /> {item.location_label}</span>}
          {reason}
        </p>
      </div>
      <StockBadge item={item} />
      <input type="number" min="1" value={q} onChange={e => setQ(e.target.value)} className="w-14 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white" data-testid={`picker-qty-${item.id}`} />
      <button onClick={() => onAdd(item, Number(q) || 1)} className="p-1.5 rounded-lg bg-amber-500 text-black" data-testid={`picker-add-${item.id}`}><Plus size={14} /></button>
    </div>
  );
};

export const InventoryPartPicker = ({ token, order, onParts }) => {
  const api = useMemo(() => invApi(token), [token]);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [sugg, setSugg] = useState([]);
  const [tick, setTick] = useState(0);
  const [showNew, setShowNew] = useState(false);
  const [searched, setSearched] = useState('');
  const noResult = q.trim() && searched === q && !results.length;
  const brandModel = `${order.vehicle_brand || ''} ${order.vehicle_model || ''}`.trim();

  useEffect(() => {
    api.get('/suggestions', { vehicle_id: order.vehicle_id || '', brand_model: brandModel }).then(setSugg).catch(() => {});
  }, [api, order.vehicle_id, brandModel, tick]);
  useEffect(() => {
    if (!q.trim()) { setResults([]); setSearched(''); return undefined; }
    const t = setTimeout(() => api.get('/items', { q }).then(r => { setResults(r); setSearched(q); }).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [api, q, tick]);

  const add = async (item, quantity) => {
    try {
      const r = await api.post(`/work-orders/${order.id}/parts`, { item_id: item.id, quantity });
      onParts(r.parts);
      setTick(t => t + 1);
      toast.success(`${item.name} ajoutée${item.location_label ? ` — emplacement ${item.location_label}` : ''}`);
      if (r.stock_after < 0) toast.info("Pièce pas en stock : l'admin est avisé de la commander");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3" data-testid="inventory-part-picker">
      <p className="flex items-center gap-2 text-sm font-semibold text-white"><Package size={16} className="text-amber-400" /> Ajouter une pièce installée</p>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-2.5 text-slate-500" />
        <input className={`${invInput} pl-8 text-xs`} placeholder="Ex. : 1 filtre LF9009" value={q} onChange={e => setQ(e.target.value)} data-testid="picker-search" />
      </div>
      {noResult && !showNew && (
        <p className="text-xs text-slate-500" data-testid="picker-no-result">Pièce absente de l'inventaire. <button onClick={() => setShowNew(true)} className="text-amber-400 underline" data-testid="picker-open-new">Inscrire « {q.trim()} »</button></p>
      )}
      {showNew && <NewPartForm api={api} orderId={order.id} initialName={q.trim()} onParts={(p) => { onParts(p); setQ(''); setTick(t => t + 1); }} onClose={() => setShowNew(false)} />}
      {!showNew && !noResult && (
        <button onClick={() => setShowNew(true)} className="text-[11px] text-slate-400 hover:text-amber-300" data-testid="picker-new-link">+ Pièce absente de l'inventaire</button>
      )}
      <div className="space-y-1.5">{results.slice(0, 8).map(it => <ItemRow key={it.id} item={it} onAdd={add} />)}</div>
      {!q.trim() && sugg.length > 0 && (
        <div className="space-y-1.5" data-testid="picker-suggestions">
          <p className="flex items-center gap-1.5 text-[11px] text-slate-400"><Sparkles size={11} className="text-amber-400" /> Suggérées pour ce véhicule</p>
          {sugg.map(s => <ItemRow key={s.item.id} item={s.item} reason={s.reason} onAdd={add} />)}
        </div>
      )}
    </div>
  );
};
